import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import {
  updatesGlobalDate,
  useAppDispatch,
  useAppSelector,
  useGetAgeChartDataQuery,
  useGetEntranceExitChartDataQuery,
  useGetKmChartDataQuery,
  useGetModelsCarsQuery,
  useGetRepairChartDataQuery,
  useGetTotalChartDataQuery,
} from '@spartalabs/pdc-core';
import {
  BarChart,
  ChartCard,
  LineChart,
  MapCard,
  MultiInfoCard,
} from 'components';
import format from 'date-fns/format';
import { useChart } from 'hooks/useChart';
import { DelayAnimation } from 'shared/DelayAnimation';
import { colors } from 'theme/components/colors';
import { ChartTypeFilters, emptyData } from 'utils/constants';
import {
  allTypesData,
  checkDateRange,
  getBarsColor,
  getFilteredColor,
  getMapColors,
  getRegionsOrStatesByRegion,
  regionDtoToRegion,
  regionToRegionDto,
  typeFilterToTypeFilterDto,
  validatesValue,
} from 'utils/functions';

export const Home = () => {
  const dispatch = useAppDispatch();
  const { globalDate, globalDateUpdate, globalPeriod } = useAppSelector(
    state => state.globalDataSlicer,
  );

  const navigate = useNavigate();

  const { handleChartData, handleChartLabels } = useChart();

  //#region <BRAZIL QUANTITIES>
  const {
    data: totalCharts,
    isLoading: isLoadingTotalCharts,
    isFetching: isFetchingTotalCharts,
    isError: isErrorTotalCharts,
    refetch: refetchTotalCharts,
  } = useGetTotalChartDataQuery({ region: undefined });
  //#endregion

  //#region <SERVICES CHARTS>
  const [pickedServicesPeriod, setPickedServicesPeriod] =
    useState(globalPeriod);
  const [servicesDateRange, setServicesDateRange] = useState(
    checkDateRange(globalDate),
  );
  const [servicesTypeFilter, setServicesTypeFilter] = useState<ChartTypeFilter>(
    ChartTypeFilters[0],
  );
  const [servicesChartLabels, setServicesChartLabels] = useState(['']);
  const [servicesChartData, setServicesChartData] = useState<
    ChartData[] | null
  >(null);

  const {
    data: servicesChartValues,
    isLoading: isLoadingServicesChart,
    isFetching: isFetchingServicesChart,
    isError: isErrorServicesChart,
    refetch: refetchServicesChart,
  } = useGetRepairChartDataQuery({
    dimension: pickedServicesPeriod,
    type: typeFilterToTypeFilterDto(servicesTypeFilter.type),
    startDate: format(
      checkDateRange(servicesDateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),

    endDate: format(
      checkDateRange(servicesDateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
  });

  useEffect(() => {
    if (servicesChartValues) {
      const labels = handleChartLabels(
        pickedServicesPeriod,
        servicesChartValues,
        servicesDateRange[0].startDate,
        servicesDateRange[0].endDate,
      );

      setServicesChartLabels(labels);

      const chart = handleChartData(
        labels,
        servicesTypeFilter.type === 'total'
          ? allTypesData(servicesChartValues.itens, labels)
          : [
              {
                label: 'Quantidade',
                values: servicesChartValues.itens,
                fill: {
                  type: 'start',
                  color: getFilteredColor(servicesTypeFilter?.type),
                },
                accessor: 'quantity',
              },
            ],
      );

      setServicesChartData(chart);
    }
  }, [servicesChartValues]);
  //#endregion

  //#region <SERVICES MULTINFO>
  const [servicesMIDateRange, setServicesMIDateRange] = useState(
    checkDateRange(globalDate),
  );

  const {
    data: servicesMultiInfoData,
    isLoading: isLoadingServicesMultiInfo,
    isFetching: isFetchingServicesMultiInfo,
    isError: isErrorServicesMultiInfo,
    refetch: refetchServicesMultiInfo,
  } = useGetRepairChartDataQuery({
    dimension: 'Mês',
    startDate: format(
      checkDateRange(servicesMIDateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),
    endDate: format(
      checkDateRange(servicesMIDateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
  });
  //#endregion

  //#region <ENTRANCE AND EXIT CHART>
  const [entranceExitPickedPeriod, setEntranceExitPickedPeriod] =
    useState(globalPeriod);
  const [entranceExitDateRange, setEntranceExitDateRange] = useState(
    checkDateRange(globalDate),
  );
  const [entranceExitChartLabels, setEntranceExitChartLabels] = useState(['']);
  const [entranceExitChartData, setEntranceExitChartData] = useState<
    ChartData[] | null
  >(null);

  const {
    data: entranceExitChartValues,
    isLoading: isLoadingEntranceExitChart,
    isFetching: isFetchingEntranceExitChart,
    isError: isErrorEntranceExitChart,
    refetch: refetchEntranceExitChart,
  } = useGetEntranceExitChartDataQuery({
    dimension: entranceExitPickedPeriod,
    endDate: format(
      checkDateRange(entranceExitDateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
    startDate: format(
      checkDateRange(entranceExitDateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),
  });

  useEffect(() => {
    if (entranceExitChartValues) {
      const labels = handleChartLabels(
        entranceExitPickedPeriod,
        entranceExitChartValues,
        entranceExitDateRange[0].startDate,
        entranceExitDateRange[0].endDate,
      );

      setEntranceExitChartLabels(labels);

      const chart = handleChartData(labels, [
        {
          label: 'Entrada',
          borderColor: colors.blue['100'],
          values: entranceExitChartValues,
          accessor: 'entrance',
        },
        {
          label: 'Saída',
          borderColor: colors.blue['200'],
          values: entranceExitChartValues,
          accessor: 'exit',
        },
      ]);

      setEntranceExitChartData(chart);
    }
  }, [entranceExitChartValues]);
  //#endregion

  //#region <AGE/KM MAP>
  const [selected, setSelected] = useState('Brasil');
  const [type, setType] = useState('Idade');

  const {
    data: ageCharts,
    isLoading: isLoadingAgeCharts,
    isFetching: isFetchingAgeCharts,
    isError: isErrorAgeCharts,
    refetch: refetchAgeCharts,
  } = useGetAgeChartDataQuery({
    region: selected === 'Brasil' ? undefined : selected?.toUpperCase(),
  });

  const { data: ageChartsSelect } = useGetAgeChartDataQuery({
    region: undefined,
  });

  const {
    data: kmCharts,
    isLoading: isLoadingKmCharts,
    isFetching: isFetchingKmCharts,
    isError: isErrorKmCharts,
    refetch: refetchKmCharts,
  } = useGetKmChartDataQuery({
    region: selected === 'Brasil' ? undefined : selected?.toUpperCase(),
  });

  const { data: kmChartsSelect } = useGetKmChartDataQuery({
    region: undefined,
  });

  const isLoadingAgeKm = useMemo(() => {
    if (type === 'Idade') return isLoadingAgeCharts || isFetchingAgeCharts;

    if (type === 'Km') return isLoadingKmCharts || isFetchingKmCharts;

    return false;
  }, [
    isLoadingAgeCharts,
    isFetchingAgeCharts,
    isLoadingKmCharts,
    isFetchingKmCharts,
  ]);

  const ageKmSelect = useMemo(() => {
    if (type === 'Idade') return ageChartsSelect;

    if (type === 'Km') return kmChartsSelect;

    return undefined;
  }, [ageCharts, kmCharts, type, selected]);

  const isErrorAgeKm = useMemo(() => {
    if (type === 'Idade') return isErrorAgeCharts;

    if (type === 'Km') return isErrorKmCharts;

    return false;
  }, [type, isErrorAgeCharts, isErrorKmCharts]);

  const refetchAgeKm = useMemo(() => {
    if (type === 'Idade') return refetchAgeCharts;

    if (type === 'Km') return refetchKmCharts;

    return undefined;
  }, [type]);

  const ageKmData = useMemo(() => {
    if (type === 'Idade') return ageCharts;

    if (type === 'Km') return kmCharts;

    return undefined;
  }, [ageCharts, kmCharts, type, selected]);

  const mapSelectLabels = useMemo(() => {
    const selectArr: string[] = [];

    Object.keys(ageKmSelect ? ageKmSelect.count : {}).map(key => {
      if (validatesValue(key, ageKmSelect.count[key])) {
        return selectArr.push(regionToRegionDto(key));
      }
    });

    return selectArr;
  }, [ageKmSelect]);
  //#endregion

  //#region <MODELS MULTINFO>
  const {
    data: modelsData,
    isFetching: isFetchingModels,
    isLoading: isLoadingModels,
    isError: isErrorModels,
    refetch: refetchModels,
  } = useGetModelsCarsQuery();
  //#endregion

  //#region <UPDATES GLOBAL DATES AND PERIODS>
  useEffect(() => {
    setEntranceExitPickedPeriod(globalPeriod);
    setPickedServicesPeriod(globalPeriod);
    if (globalDateUpdate) {
      setEntranceExitDateRange(globalDate);
      setServicesMIDateRange(globalDate);
      setServicesDateRange(globalDate);
      dispatch(updatesGlobalDate(false));
    }
  }, [globalDate, globalDateUpdate]);

  useEffect(() => {
    setEntranceExitPickedPeriod(globalPeriod);
    setPickedServicesPeriod(globalPeriod);
    setEntranceExitDateRange(globalDate);
    setServicesMIDateRange(globalDate);
    setServicesDateRange(globalDate);
  }, [globalPeriod]);
  //#endregion

  return (
    <DelayAnimation>
      <Box
        px={{ base: '16px', md: '32px' }}
        pb="20px"
        flex={1}
        minH="full"
        fontSize="xl"
      >
        <Flex w={100} flex={1}></Flex>
        <Stack
          mb="20px"
          spacing="20px"
          direction={{ base: 'column', md: 'row' }}
        >
          <MapCard
            cardName="regions"
            loading={isLoadingTotalCharts || isFetchingTotalCharts}
            details={() => navigate('/relatorios/distdefrotas')}
            subtitleMb="20px"
            minH="100%"
            w={{ base: '100%', md: '34%' }}
            mapW="300px"
            mapType="Brazil"
            mapColor={getMapColors(totalCharts && totalCharts.count)}
            hasMap
            customTitle={
              <Text
                alignSelf="center"
                color="text.100"
                fontSize="16px"
                fontWeight={400}
              >
                Regiões do <b>Brasil</b>
              </Text>
            }
            subtitle="Distribuição de frota total pelo Brasil"
            data={getRegionsOrStatesByRegion(
              totalCharts && totalCharts.count,
              'Brazil',
            )}
            isError={isErrorTotalCharts}
            refetch={refetchTotalCharts}
          />

          <ChartCard
            cardName="services"
            details={() => navigate('/relatorios/manutencao/servicos')}
            pickerType="date"
            picker={['Dia', 'Mês']}
            firstPicked={pickedServicesPeriod}
            setPicked={setPickedServicesPeriod}
            startDate={servicesDateRange}
            setDateRange={setServicesDateRange}
            setFilter={setServicesTypeFilter}
            filters={ChartTypeFilters}
            w={{ base: '100%', md: '66%' }}
            subtitleMb="20px"
            customTitle={
              <Text
                alignSelf="center"
                color="text.100"
                fontSize="16px"
                fontWeight={400}
              >
                Gráfico de<b> Serviços</b>
              </Text>
            }
            subtitle="Comparativo com os principais serviços realizados. Se preferir filtre pelos serviços abaixo"
            isError={isErrorServicesChart}
            refetch={refetchServicesChart}
          >
            <Flex w="100%" overflow="auto" justify="center">
              <BarChart
                stackedY={true}
                stackedX={true}
                loading={isLoadingServicesChart || isFetchingServicesChart}
                w="800px"
                labels={servicesChartLabels}
                data={servicesChartData || emptyData}
              />
            </Flex>
          </ChartCard>
        </Stack>
        <Stack
          mb="20px"
          spacing="20px"
          direction={{ base: 'column', xl: 'row' }}
        >
          <MultiInfoCard
            cardName="total-services"
            loading={isLoadingServicesMultiInfo || isFetchingServicesMultiInfo}
            showTotal={true}
            setDateRange={setServicesMIDateRange}
            startDate={servicesMIDateRange}
            w="100%"
            title="Atendimentos Realizados"
            subtitle="Quantidade por tipo de serviço"
            data={servicesMultiInfoData}
            isError={isErrorServicesMultiInfo}
            refetch={refetchServicesMultiInfo}
          />
        </Stack>

        <ChartCard
          cardName="exit-entrace"
          details={() => navigate('/relatorios/manutencao/entradasaida')}
          pickerType="date"
          picker={['Dia', 'Mês']}
          firstPicked={entranceExitPickedPeriod}
          setPicked={setEntranceExitPickedPeriod}
          startDate={entranceExitDateRange}
          setDateRange={setEntranceExitDateRange}
          mb="20px"
          from="home"
          customTitle={
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              Entrada e Saída de<b> Manutenções</b>
            </Text>
          }
          subtitle="Comparativo sobre a relação entre entrada e saída de veículos em manutenções"
          w="100%"
          isError={isErrorEntranceExitChart}
          refetch={refetchEntranceExitChart}
        >
          <Flex w="100%" overflow="auto" justify="center">
            <LineChart
              loading={
                isLoadingEntranceExitChart || isFetchingEntranceExitChart
              }
              w="800px"
              labels={entranceExitChartLabels}
              data={entranceExitChartData || emptyData}
            />
          </Flex>
        </ChartCard>

        <MapCard
          cardName="map"
          details={() => navigate('/relatorios/distdefrotas')}
          loading={isLoadingAgeKm}
          setSelected={setSelected}
          mapColor={
            selected === 'Brasil'
              ? getMapColors(ageKmSelect?.count)
              : {
                  ...getMapColors(ageKmSelect?.count, 'Brasil'),
                  [regionDtoToRegion(selected)]: true,
                }
          }
          isMapClickable
          mb="20px"
          w="100%"
          select={mapSelectLabels}
          hasMap
          mapW="250px"
          mapH="250px"
          hasBars
          setPicked={setType}
          barsColor={getBarsColor(selected, type)}
          picker={['Idade', 'Km']}
          customTitle={
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              Regiões por<b> Idade e Km - Regiões</b>
            </Text>
          }
          title=" "
          subtitle="Separada em cada estado como estão distribuídos por idade e km a sua frota"
          data={
            ageKmData?.count[
              selected === 'Brasil' ? 'br' : regionDtoToRegion(selected)
            ] || {}
          }
          isError={isErrorAgeKm}
          refetch={refetchAgeKm}
        />

        <MultiInfoCard
          cardName="models"
          w="100%"
          customTitle={
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              Distribuição da Frota por<b> Modelo</b>
            </Text>
          }
          subtitle="Como estão distribuídos os modelos em sua frota"
          details={() => navigate('/relatorios/modelos')}
          loading={isFetchingModels || isLoadingModels}
          data={modelsData}
          isError={isErrorModels}
          refetch={refetchModels}
        />
      </Box>
    </DelayAnimation>
  );
};
